import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, DocumentTextIcon, ExclamationTriangleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import falabellaService from '../../api/falabellaService'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface FalabellaShippingLabelModalProps {
  isOpen: boolean
  onClose: () => void
  ordersId: string | number
  orderNumber?: string
}

const FalabellaShippingLabelModal: React.FC<FalabellaShippingLabelModalProps> = ({
  isOpen,
  onClose,
  ordersId,
  orderNumber
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [labelData, setLabelData] = useState<any>(null)
  const [hasSuccessfulLoad, setHasSuccessfulLoad] = useState(false)

  const handleGetShippingLabel = useCallback(async () => {
    const ordersIdStr = String(ordersId || '').trim()
    if (!ordersIdStr) {
      toast.error('ID de pedido es requerido')
      return
    }

    setIsLoading(true)
    setError(null)
    setLabelData(null)

    try {
      const response = await falabellaService.getShippingLabelByOrderId(
        ordersIdStr,
        'JSON'
      )

      if (response.success && response.data) {
        setLabelData(response.data)
        setHasSuccessfulLoad(true)
        if (!isInitialLoad) {
          toast.success('Etiqueta de envío obtenida exitosamente')
        }
      } else {
        setError(response.message || 'No se pudo obtener la etiqueta de envío')
        setHasSuccessfulLoad(false)
        if (!isInitialLoad) {
          toast.error(response.message || 'Error al obtener la etiqueta')
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al consultar la etiqueta de envío'
      setError(errorMessage)
      setHasSuccessfulLoad(false)
      if (!isInitialLoad) {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
      setIsInitialLoad(false)
    }
  }, [ordersId, isInitialLoad])

  // Consultar automáticamente al abrir el modal
  useEffect(() => {
    const ordersIdStr = String(ordersId || '').trim()
    if (isOpen && ordersIdStr) {
      handleGetShippingLabel()
    }
  }, [isOpen, ordersId, handleGetShippingLabel])

  const handleClose = () => {
    setLabelData(null)
    setError(null)
    setIsInitialLoad(true)
    setHasSuccessfulLoad(false)
    onClose()
  }

  // Función alternativa para decodificar base64 de manera más robusta
  const decodeBase64 = (str: string): string => {
    try {
      // Método 1: atob nativo
      return atob(str)
    } catch (e) {
      try {
        // Método 2: usando Buffer si está disponible
        if (typeof Buffer !== 'undefined') {
          return Buffer.from(str, 'base64').toString('binary')
        }
        // Método 3: decodificación manual
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
        let result = ''
        let i = 0
        while (i < str.length) {
          const encoded1 = chars.indexOf(str.charAt(i++))
          const encoded2 = chars.indexOf(str.charAt(i++))
          const encoded3 = chars.indexOf(str.charAt(i++))
          const encoded4 = chars.indexOf(str.charAt(i++))
          
          const bitmap = (encoded1 << 18) | (encoded2 << 12) | (encoded3 << 6) | encoded4
          
          result += String.fromCharCode((bitmap >> 16) & 255)
          if (encoded3 !== 64) result += String.fromCharCode((bitmap >> 8) & 255)
          if (encoded4 !== 64) result += String.fromCharCode(bitmap & 255)
        }
        return result
      } catch (e2) {
        throw new Error('No se pudo decodificar el base64: ' + e2)
      }
    }
  }

  const downloadPDF = () => {
    console.log('Datos de la etiqueta:', labelData)
    
    // Buscar el archivo en la estructura correcta de la respuesta
    let fileData = null
    let mimeType = 'application/pdf'
    
    // Estructura real de la respuesta de Falabella
    if (labelData?.falabellaResponse?.SuccessResponse?.Body?.Documents?.Document?.File) {
      fileData = labelData.falabellaResponse.SuccessResponse.Body.Documents.Document.File
      mimeType = labelData.falabellaResponse.SuccessResponse.Body.Documents.Document.MimeType || 'application/pdf'
    } 
    // Fallbacks para otras posibles estructuras
    else if (labelData?.labelInfo?.file) {
      fileData = labelData.labelInfo.file
      mimeType = labelData.labelInfo.mimeType || 'application/pdf'
    } else if (labelData?.falabellaResponse?.SuccessResponse?.Body?.File) {
      fileData = labelData.falabellaResponse.SuccessResponse.Body.File
      mimeType = labelData.falabellaResponse.SuccessResponse.Body.MimeType || 'application/pdf'
    } else if (labelData?.data?.falabellaResponse?.SuccessResponse?.Body?.Documents?.Document?.File) {
      fileData = labelData.data.falabellaResponse.SuccessResponse.Body.Documents.Document.File
      mimeType = labelData.data.falabellaResponse.SuccessResponse.Body.Documents.Document.MimeType || 'application/pdf'
    }

    if (!fileData) {
      console.error('No se encontró archivo en los datos:', labelData)
      toast.error('No hay archivo PDF disponible para descargar')
      return
    }

    try {
      console.log('Tipo MIME:', mimeType)
      console.log('Datos del archivo (primeros 100 caracteres):', fileData.substring(0, 100))
      console.log('Longitud del base64:', fileData.length)
      
      // Validar que el base64 no esté vacío
      if (!fileData || fileData.trim() === '') {
        throw new Error('El archivo base64 está vacío')
      }
      
      // Limpiar el base64 de posibles caracteres de espacio o saltos de línea
      const cleanBase64 = fileData.replace(/\s/g, '')
      console.log('Base64 limpio (primeros 100 caracteres):', cleanBase64.substring(0, 100))
      
      // Validar que sea base64 válido
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
      if (!base64Regex.test(cleanBase64)) {
        throw new Error('El archivo no es un base64 válido')
      }
      
      // Decodificar el base64 usando función robusta
      const pdfData = decodeBase64(cleanBase64)
      console.log('Datos decodificados (primeros 100 caracteres):', pdfData.substring(0, 100))
      console.log('Tamaño de datos decodificados:', pdfData.length)
      
      // Validar que los datos decodificados no estén vacíos
      if (!pdfData || pdfData.length === 0) {
        throw new Error('Los datos decodificados están vacíos')
      }
      
      // Validar que sea un PDF válido (debe empezar con %PDF)
      if (mimeType === 'application/pdf' && !pdfData.startsWith('%PDF')) {
        console.warn('Advertencia: Los datos no parecen ser un PDF válido')
        console.log('Primeros caracteres:', pdfData.substring(0, 20))
      }
      
      // Crear un blob con el archivo usando Uint8Array para mejor compatibilidad
      const bytes = new Uint8Array(pdfData.length)
      for (let i = 0; i < pdfData.length; i++) {
        bytes[i] = pdfData.charCodeAt(i)
      }
      
      const blob = new Blob([bytes], { type: mimeType })
      console.log('Blob creado:', blob.size, 'bytes')
      console.log('Tipo de blob:', blob.type)
      
      // Validar que el blob tenga contenido
      if (blob.size === 0) {
        throw new Error('El archivo generado está vacío')
      }
      
      // Crear URL del blob
      const url = window.URL.createObjectURL(blob)
      console.log('URL creada:', url)
      
      // Probar si el PDF se puede abrir en una nueva ventana (opcional)
      const testWindow = window.open(url, '_blank')
      if (testWindow) {
        console.log('PDF abierto en nueva ventana para prueba')
        // Cerrar la ventana de prueba después de 2 segundos
        setTimeout(() => {
          testWindow.close()
        }, 2000)
      }
      
      // Determinar extensión del archivo según el MIME type
      const fileExtension = mimeType === 'application/pdf' ? 'pdf' : 'zpl'
      
      // Crear elemento de descarga
      const link = document.createElement('a')
      link.href = url
      link.download = `etiqueta-envio-${String(ordersId || '')}.${fileExtension}`
      link.style.display = 'none'
      
      // Simular click para descargar
      document.body.appendChild(link)
      link.click()
      
      // Limpiar después de un breve delay
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)
      
      const fileType = mimeType === 'application/pdf' ? 'PDF' : 'ZPL'
      toast.success(`Etiqueta de envío ${fileType} descargada exitosamente`)
    } catch (err) {
      console.error('Error descargando PDF:', err)
      console.error('Datos del archivo:', fileData)
      console.error('Tipo MIME:', mimeType)
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
      toast.error('Error al descargar el archivo: ' + errorMessage)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Etiqueta de Envío
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Info */}
          <div className="mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-green-800">Pedido:</span>
                <span className="text-sm text-green-600 font-mono">
                  {orderNumber || String(ordersId || '')}
                </span>
              </div>
{/*               <div className="text-xs text-green-600">
                ID de pedido: {ordersId}
              </div> */}
             {/*  <div className="text-xs text-green-600 mt-1">
                Formato: JSON
              </div> */}
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="mb-6">
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <LoadingSpinner size="large" color="text-green-600" />
                  <p className="text-sm text-gray-600 mt-2">
                    Generando etiqueta de envío...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Button - Solo mostrar si no está cargando y no hubo carga exitosa */}
          {!isLoading && !hasSuccessfulLoad && (
            <div className="mb-6">
              <button
                onClick={handleGetShippingLabel}
                disabled={!String(ordersId || '').trim()}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <DocumentTextIcon className="h-4 w-4" />
                Generar Etiqueta Nuevamente
              </button>
            </div>
          )}

          {/* Results */}
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium text-red-800">Error</span>
                </div>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {labelData && (
            <div className="mb-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DocumentTextIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium text-green-800">
                    Etiqueta de Envío Generada
                  </span>
                </div>
                
                <div className="space-y-3">
{/*                   <div>
                    <span className="text-xs font-medium text-green-700 block">Tipo de Documento:</span>
                    <span className="text-sm text-green-800">
                      {labelData.documentType || 'shippingParcel'}
                    </span>
                  </div> */}
                  
{/*                   <div>
                    <span className="text-xs font-medium text-green-700 block">Items en el pedido:</span>
                    <span className="text-sm text-green-800">
                      {labelData.itemsCount}
                    </span>
                  </div> */}
                  
{/*                   <div>
                    <span className="text-xs font-medium text-green-700 block">Tipo de Documento:</span>
                    <span className="text-sm text-green-800">
                      {labelData.documentType || 'shippingParcel'}
                    </span>
                  </div> */}
                  
{/*                   <div>
                    <span className="text-xs font-medium text-green-700 block">Formato:</span>
                    <span className="text-sm text-green-800">
                      {labelData.format}
                    </span>
                  </div> */}
                  
{/*                   {labelData.orderItemIds && labelData.orderItemIds.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-green-700 block">IDs de Items:</span>
                      <div className="text-xs text-green-600 font-mono">
                        {labelData.orderItemIds.join(', ')}
                      </div>
                    </div>
                  )} */}

                  {/* Información de debug */}
{/*                   <div className="pt-3 border-t border-green-200">
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded mb-2 max-h-20 overflow-y-auto">
                      <pre>{JSON.stringify(labelData, null, 2).substring(0, 500)}...</pre>
                    </div>
                  </div> */}

                  {/* Botón de descarga */}
                  <div className="pt-3 border-t border-green-200">
                    <button
                      onClick={downloadPDF}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Descargar PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default FalabellaShippingLabelModal
